import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { usersAPI } from '../services/api';
import { User, Search, ChevronDown, X } from 'lucide-react';

const UserDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Select user...", 
  disabled = false,
  excludeUserId = null 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const dropdownRef = useRef(null);

  // Get all active users
  const { data: usersData, isLoading } = useQuery('activeUsers', () => usersAPI.getActive());

  // Filter users based on search term and exclude current user
  useEffect(() => {
    if (usersData?.users) {
      const filtered = usersData.users.filter(user => {
        const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
        const isNotExcluded = excludeUserId ? user.id !== excludeUserId : true;
        return matchesSearch && isNotExcluded;
      });
      setFilteredUsers(filtered);
    }
  }, [usersData, searchTerm, excludeUserId]);

  // Get selected user
  const selectedUser = usersData?.users?.find(user => user.id === value);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUserSelect = (user) => {
    onChange(user.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full px-4 py-3 border rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer ${
          disabled 
            ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed' 
            : 'bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {selectedUser ? (
              <>
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-3 w-3 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{selectedUser.full_name}</p>
                  {selectedUser.email && (
                    <p className="text-xs text-gray-600">{selectedUser.email}</p>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {selectedUser && !disabled && (
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading users...</p>
              </div>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{user.full_name}</p>
                    {user.email && (
                      <p className="text-sm text-gray-600">{user.email}</p>
                    )}
                    {user.roles && user.roles.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {user.roles.map(role => role.display_name).join(', ')}
                      </p>
                    )}
                  </div>
                  {value === user.id && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center">
                <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {searchTerm ? 'No users found matching your search' : 'No users available'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown; 